// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { unwrap } from "@granite/api-client";
import { passwordAcceptable } from "@granite/core";
import { Button, TextField } from "@granite/ui";
import { useMutation } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { useState, type SubmitEvent } from "react";

import { AuthLayout, FormAlert, linkClass } from "../components/AuthLayout.js";
import { OtherSignIn } from "../components/OtherSignIn.js";
import { PasswordStrength } from "../components/PasswordStrength.js";
import { errorMessage, fieldErrors } from "../lib/errors.js";
import { t } from "../lib/i18n.js";
import { authClient } from "../lib/session.js";

const route = getRouteApi("/signup");

type Kind = "personal" | "business";

export function SignupPage() {
  const navigate = useNavigate();
  const { plan } = route.useSearch();
  // A business account starts with its company's workspace, which it owns.
  const [kind, setKind] = useState<Kind>(plan === "business" ? "business" : "personal");
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [weak, setWeak] = useState(false);
  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        email: email.trim(),
        password,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(kind === "business" ? { workspace_name: company.trim() } : {}),
      };
      unwrap(await authClient.POST("/v1/auth/signup", { body }));
    },
    onSuccess: () => navigate({ to: "/check-email", search: { email: email.trim() } }),
  });
  const fields = fieldErrors(mutation.error);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    // The server refuses it too; saying so here saves the round trip.
    if (!passwordAcceptable(password, [email, name])) {
      setWeak(true);
      return;
    }
    mutation.mutate();
  };

  return (
    <AuthLayout
      title={kind === "business" ? t("signup.businessTitle") : t("signup.title")}
      footer={
        <>
          {t("signup.haveAccount")}{" "}
          <Link to="/login" className={linkClass}>
            {t("common.signIn")}
          </Link>
        </>
      }
    >
      {mutation.error && <FormAlert tone="error">{errorMessage(mutation.error)}</FormAlert>}
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div role="radiogroup" aria-label={t("signup.kind")} className="grid grid-cols-2 gap-1 rounded-lg bg-surface-muted p-1">
          {(["personal", "business"] as const).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              className={
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
                (kind === k ? "bg-surface text-fg shadow-sm" : "text-fg-muted hover:text-fg")
              }
              onClick={() => {
                setKind(k);
              }}
            >
              {t(k === "business" ? "signup.kind.business" : "signup.kind.personal")}
            </button>
          ))}
        </div>
        {kind === "business" && (
          <TextField
            label={t("signup.company")}
            description={t("signup.companyHint")}
            autoComplete="organization"
            maxLength={100}
            required
            value={company}
            error={fields.workspace_name}
            onChange={(e) => {
              setCompany(e.target.value);
            }}
          />
        )}
        <TextField
          label={t("signup.name")}
          description={t("signup.nameHint")}
          autoComplete="name"
          maxLength={200}
          value={name}
          error={fields.name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <TextField
          label={t("common.email")}
          type="email"
          autoComplete="email"
          required
          value={email}
          error={fields.email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
        <TextField
          label={t("common.password")}
          description={t("signup.passwordHint")}
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={password}
          error={weak ? t("password.tooWeak") : fields.password}
          onChange={(e) => {
            setPassword(e.target.value);
            setWeak(false);
          }}
        />
        <PasswordStrength password={password} personal={[email, name]} />
        <Button type="submit" loading={mutation.isPending} disabled={kind === "business" && !company.trim()}>
          {t("signup.submit")}
        </Button>
        <p className="text-center text-xs text-fg-muted">
          {t("signup.agreeBefore")}{" "}
          <a className="g-link" href="https://granite.md/terms" target="_blank" rel="noreferrer">
            {t("signup.terms")}
          </a>{" "}
          {t("signup.agreeAnd")}{" "}
          <a className="g-link" href="https://granite.md/privacy" target="_blank" rel="noreferrer">
            {t("signup.privacy")}
          </a>
          .
        </p>
      </form>
      <OtherSignIn next="/" {...(kind === "business" ? { business: { workspaceName: company } } : {})} />
    </AuthLayout>
  );
}
